import type { ReactNode } from "react";
import { formatMoney, stepPath, type ChartFrame, type DatedValue, type ProfitStack } from "../portfolio-tracker";
import { EndMark, Gridlines, HoverMarks, TimeTicks } from "./chart-parts";

interface StackedAreaProps {
  frame: ChartFrame;
  putIn: number;
  balance: DatedValue[];
  stack: ProfitStack;
  colours: Map<string, string>;
  zone: ReactNode;
  hoverDay: string | null;
  hoverValue: number;
}

/** What was put in as a grey base, each strategy's profit so far stacked on it, and the balance as a line. */
export function StackedArea({ frame, putIn, balance, stack, colours, zone, hoverDay, hoverValue }: StackedAreaProps) {
  const xs = stack.dates.map(frame.x);
  const steps = (values: number[]) => {
    const points: [number, number][] = [[xs[0], frame.y(values[0])]];
    for (let index = 1; index < xs.length; index++) {
      points.push([xs[index], frame.y(values[index - 1])], [xs[index], frame.y(values[index])]);
    }
    return points;
  };
  const pathOf = (points: [number, number][]) =>
    points.map(([x, y], index) => `${index ? "L" : "M"}${x.toFixed(1)},${y.toFixed(1)}`).join("");
  const area = (top: number[], bottom: number[]) => `${pathOf(steps(top).concat(steps(bottom).reverse()))}Z`;

  const shapes: ReactNode[] = [];
  const gaps: ReactNode[] = [];
  stack.bands.forEach((band) => {
    const fill = colours.get(band.id);
    if (band.gains[1].some((value, index) => value - band.gains[0][index] > 0.5)) {
      shapes.push(<path key={`${band.id}-up`} className="tracker-band" style={{ fill }} d={area(band.gains[1], band.gains[0])} />);
      gaps.push(<path key={`${band.id}-up-gap`} className="tracker-band-gap" d={pathOf(steps(band.gains[1]))} />);
    }
    if (band.losses[1].some((value, index) => band.losses[0][index] - value > 0.5)) {
      shapes.push(<path key={`${band.id}-down`} className="tracker-band" style={{ fill }} d={area(band.losses[0], band.losses[1])} />);
      gaps.push(<path key={`${band.id}-down-gap`} className="tracker-band-gap" d={pathOf(steps(band.losses[1]))} />);
    }
  });

  const last = balance[balance.length - 1];
  const totalPath = stepPath(balance.map((point) => ({ x: frame.x(point.date), y: frame.y(point.value) })));

  return (
    <>
      <Gridlines frame={frame} />
      <rect
        className="tracker-band-base"
        x={xs[0]}
        y={frame.y(putIn)}
        width={xs[xs.length - 1] - xs[0]}
        height={frame.y(0) - frame.y(putIn)}
      />
      {shapes}
      {gaps}
      {zone}
      <path className="tracker-chart-total" d={totalPath} />
      <TimeTicks frame={frame} from={stack.dates[0]} to={last.date} />
      <EndMark x={frame.x(last.date)} y={frame.y(last.value)} label={formatMoney(last.value)} />
      {hoverDay && <HoverMarks frame={frame} x={frame.x(hoverDay)} y={frame.y(hoverValue)} />}
    </>
  );
}
